import { useRouterState } from '@tanstack/react-router';
import { useSyncExternalStore } from 'react';
import { z } from 'zod';

import {
  activityView,
  allActivities,
  completionOf,
  currentModuleOf,
  nextActivity,
  statusOf,
  moduleView,
} from '@/shared/program/derive';
import { useProgramFull } from '@/shared/program/use-program-full';

import type { MioContext, ProgramFull } from '@mio/shared';

const surfaceSchema = z.object({
  activity: z.object({
    id: z.string(),
    code: z.string(),
    title: z.string(),
    kind: z.literal('workshop'),
    status: z.enum(['not_started', 'in_progress', 'complete']),
  }),
  transition: z
    .object({ requestedActivityId: z.string(), state: z.enum(['pending', 'preview']) })
    .optional(),
});

function publishedWorkshopSurface() {
  return (
    document.querySelector<HTMLElement>('[data-mio-workshop-surface]')?.dataset
      .mioWorkshopSurface ?? ''
  );
}

function observeWorkshopSurface(notify: () => void) {
  const observer = new MutationObserver(notify);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-mio-workshop-surface'],
  });
  return () => observer.disconnect();
}

function environmentOf(pathname: string): MioContext['environment'] {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/studio')) return 'studio';
  if (pathname.startsWith('/workshop')) return 'workshop';
  if (pathname.startsWith('/field')) return 'field';
  return 'other';
}

/** What Mio can see: derived from the current route and the cached program, no extra requests. */
export function usePageContext(): MioContext | null {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const surface = useSyncExternalStore(observeWorkshopSurface, publishedWorkshopSurface, () => '');
  const { data } = useProgramFull((program) => pageContext(program, pathname, surface));
  return data ?? null;
}

/** Activity identity follows the published visible brief, while route records the destination. */
export function pageContext(
  program: ProgramFull,
  pathname: string,
  publishedSurface = '',
): MioContext {
  let surface: z.infer<typeof surfaceSchema> | null = null;
  if (pathname.startsWith('/workshop') && publishedSurface) {
    try {
      surface = surfaceSchema.parse(JSON.parse(publishedSurface));
    } catch {
      /* Ignore malformed browser hints. */
    }
  }
  const activityMatch = /^\/(?:studio|workshop)\/([0-9a-f-]{36})$/.exec(pathname);
  const currentModule = currentModuleOf(program);
  const visibleId = surface?.activity.id ?? activityMatch?.[1];
  const activity = visibleId ? activityView(program, visibleId) : null;
  const moduleMatch = /^\/studio\/modules\/(\d+)$/.exec(pathname);
  const moduleNumber =
    activity?.module.number ?? (moduleMatch?.[1] ? Number(moduleMatch[1]) : currentModule);
  const module = surface && !activity ? null : moduleView(program, moduleNumber);
  const all = allActivities(program);
  const next = nextActivity(program);
  const context: MioContext = {
    route: pathname,
    environment: environmentOf(pathname),
    currentModule,
    totalModules: program.modules.length,
    completionPercent: completionOf(program),
    completedCount: all.filter((a) => statusOf(program, a.id) === 'complete').length,
    activityCount: all.length,
    module: module
      ? { number: module.number, title: module.title, mioStance: module.mioStance }
      : null,
    activity:
      surface?.activity ??
      (activity
        ? {
            id: activity.id,
            code: activity.code,
            kind: activity.kind,
            title: activity.title,
            status: activity.status,
          }
        : null),
    ...(surface?.transition ? { workshopTransition: surface.transition } : {}),
    nextActivity:
      next && !(surface && !activity)
        ? { id: next.id, code: next.code, kind: next.kind, title: next.title }
        : null,
  };
  return context;
}
